// Placas Arduino. Las posiciones de los pines siguen la plantilla oficial de shields del Uno R3
// (milésimas de pulgada desde la esquina inferior izquierda) convertidas a mm con origen al centro.
import { definePart, PITCH } from './registry.js';

const MIL = 0.0254;

// Uno R3 -------------------------------------------------------------------------------------------
export const UNO = {
  W: 68.58,
  D: 53.34,
  PCB_Y: 1.6,        // la placa descansa sobre las puntas de los pines
  PCB_T: 1.6,
  HEADER_H: 8.5,
};
UNO.PCB_TOP = UNO.PCB_Y + UNO.PCB_T;
UNO.PIN_Y = UNO.PCB_TOP + UNO.HEADER_H;

const unoX = (xm) => (xm - 1350) * MIL;
const unoZ = (ym) => -(ym - 1050) * MIL;

const PWM = new Set(['3', '5', '6', '9', '10', '11']);
// VIN es la entrada del regulador (7–12 V): ahí se conecta una batería o fuente.
const VIN_LABEL = 'VIN · entrada 7–12 V';

// Alimentación (ver client/sim/power.js). Cable USB: desconectarlo en plena simulación apaga la placa si
// nada más la alimenta; al volver la energía el programa arranca desde cero.
const USB_PROP = { key: 'usb', label: 'Cable USB conectado', type: 'boolean', default: true, runtime: true };

// ATmega328P y ATmega2560 con los fusibles de fábrica de Arduino (extendido 0xFD: brown-out en 2.7 V con
// 50 mV de histéresis; bajo 0xFF: arranque de 16K ciclos + 65 ms tras el reset). A 16 MHz el 328P necesita
// 3.78 V (la curva de frecuencia máxima va de 10 MHz a 2.7 V a 20 MHz a 4.5 V) y el 2560, 4.5 V.
const MCU_328 = { chip: 'ATmega328P', bod: 2.7, hyst: 0.05, vmin: 3.78, vmax: 5.5, vabs: 6, startup: 0.066 };
const MCU_2560 = { chip: 'ATmega2560', bod: 2.7, hyst: 0.05, vmin: 4.5, vmax: 5.5, vabs: 6, startup: 0.066 };

/**
 * Uno R3 y Mega 2560 R3 (esquemáticos Rev3e): USB → fusible MF-MSMF050 → MOSFET P (FDN340P en el Uno, 60 mΩ;
 * PMV48XP en el Mega, 48 mΩ; típicos con VGS = −4.5 V) → 5V; un comparador LMV358 abre el MOSFET cuando VIN/2
 * pasa de 3.3 V (VIN > 6.6 V) y su diodo interno (0.7 V) sigue dejando pasar si el 5V cae. VIN → regulador
 * 1117 de 5 V; 5V → LP2985 de 3.3 V.
 * θJA del regulador ≈ 85 °C/W: tabla de cobre de la hoja del LM1117 (SOT-223) para ~1.5 cm² por cara, lo que
 * tiene el +5V junto al regulador en estas placas.
 */
function arduinoR3Supply({ ron, reg, load, led, mcu }) {
  return {
    rails: {
      '5V': { pins: ['5V', 'IOREF', '5V.2', '5V.3'], v: 5, cap: 47e-6 },
      '3.3V': { pins: ['3.3V'], v: 3.3, cap: 1e-6 },
    },
    mcu: { rail: '5V', load, ...mcu },
    usb: { rail: '5V', v: 5, fuse: 'MF-MSMF050', switch: { ron, vd: 0.7, rd: 0.5, offAbove: { pin: 'VIN', v: 6.6 } } },
    regulators: [
      { part: reg, from: { pin: 'VIN' }, to: '5V', rja: 85 },
      { part: 'LP2985-3.3', from: { rail: '5V' }, to: '3.3V' },
    ],
    vinLoad: 20000, // divisor del comparador (2 × 10 kΩ)
    led: { rail: '5V', r: led },
  };
}

function digitalLabel(n) {
  const extra = {
    0: 'RX', 1: 'TX', 2: 'INT0', 3: 'INT1', 10: 'SS', 11: 'MOSI', 12: 'MISO', 13: 'SCK · LED L',
  }[n];
  return `D${n}${PWM.has(String(n)) ? ' ~PWM' : ''}${extra ? ' · ' + extra : ''}`;
}

const unoPins = [];
{
  const top = [
    ['SCL', 840, 'A5 · I²C SCL', 'signal'], ['SDA', 940, 'A4 · I²C SDA', 'signal'], ['AREF', 1040, 'AREF', 'power'],
    ['GND.1', 1140, 'GND', 'gnd'],
  ];
  for (let n = 13; n >= 8; n--) top.push([String(n), 1240 + (13 - n) * 100, digitalLabel(n), PWM.has(String(n)) ? 'pwm' : 'digital']);
  for (let n = 7; n >= 0; n--) top.push([String(n), 1900 + (7 - n) * 100, digitalLabel(n), PWM.has(String(n)) ? 'pwm' : 'digital']);
  for (const [name, xm, label, kind] of top) unoPins.push({ name, label, kind, x: unoX(xm), y: UNO.PIN_Y, z: unoZ(2000) });
  const bottom = [
    ['IOREF', 1100, 'IOREF (5 V)', 'power'], ['RESET', 1200, 'RESET', 'signal'], ['3.3V', 1300, '3.3 V', 'power'],
    ['5V', 1400, '5 V', 'power'], ['GND.2', 1500, 'GND', 'gnd'], ['GND.3', 1600, 'GND', 'gnd'], ['VIN', 1700, VIN_LABEL, 'power'],
  ];
  for (let a = 0; a <= 5; a++) bottom.push([`A${a}`, 1900 + a * 100, `A${a}${a === 4 ? ' · SDA' : a === 5 ? ' · SCL' : ''}`, 'analog']);
  for (const [name, xm, label, kind] of bottom) unoPins.push({ name, label, kind, x: unoX(xm), y: UNO.PIN_Y, z: unoZ(100) });
}

const AVR328_PINS = {
  0: 'PD0', 1: 'PD1', 2: 'PD2', 3: 'PD3', 4: 'PD4', 5: 'PD5', 6: 'PD6', 7: 'PD7',
  8: 'PB0', 9: 'PB1', 10: 'PB2', 11: 'PB3', 12: 'PB4', 13: 'PB5',
  A0: 'PC0', A1: 'PC1', A2: 'PC2', A3: 'PC3', A4: 'PC4', A5: 'PC5',
};

definePart({
  type: 'arduino-uno',
  name: 'Arduino Uno R3',
  help: 'Los pines con ~ tienen PWM. I²C: A4 (SDA) y A5 (SCL). SPI: 10 (SS), 11 (MOSI), 12 (MISO), 13 (SCK). Interrupciones externas: 2 y 3. ' +
    '<b>Alimentación</b>: USB (fusible de 500 mA) o 7–12 V entre VIN y GND (regulador NCP1117; con más de 6.6 V en VIN deja de usar el USB). ' +
    'El regulador se calienta con (VIN − 5 V) × corriente y da hasta ~1.5 A; el pin 3.3V, 150 mA. Abajo de 2.7 V el ATmega queda en reset (brown-out). ' +
    'Sin cable USB no hay monitor serie.',
  category: 'placas',
  description: 'Placa con ATmega328P a 16 MHz: 14 pines digitales (6 PWM), 6 entradas analógicas, Serial, I²C y SPI.',
  kind: 'board',
  props: [USB_PROP],
  pins: unoPins,
  buses: [['A4', 'SDA'], ['A5', 'SCL'], ['GND.1', 'GND.2', 'GND.3'], ['5V', 'IOREF']],
  footprint: { w: UNO.W + 8, d: UNO.D, h: 14, x: -3 },
  mcu: {
    chip: 'atmega328p',
    fqbn: 'arduino:avr:uno',
    pins: AVR328_PINS,
    adc: { A0: 0, A1: 1, A2: 2, A3: 3, A4: 4, A5: 5 },
    power: { '5V': 5, IOREF: 5, '3.3V': 3.3, 'GND.1': 0, 'GND.2': 0, 'GND.3': 0 },
    // Consumo propio medido de un Uno R3 con un sketch vacío: 49 mA en el pin 5V (N. Gammon). LED ON con 2 × 1 kΩ.
    supply: arduinoR3Supply({ ron: 0.06, reg: 'NCP1117-5.0', load: 0.049, led: 500, mcu: MCU_328 }),
    vin: 'VIN',
    aref: 'AREF',
    reset: 'RESET',
    builtinLed: '13',
    bus: { sda: 'A4', scl: 'A5', sck: '13', mosi: '11', miso: '12', ss: '10' },
  },
});

// Nano 3 -------------------------------------------------------------------------------------------
export const NANO = { W: 43.18, D: 17.78, PCB_Y: 2.6, PCB_T: 1.6 };
NANO.PCB_TOP = NANO.PCB_Y + NANO.PCB_T;

const nanoTop = ['12', '11', '10', '9', '8', '7', '6', '5', '4', '3', '2', 'GND.1', 'RESET.1', '0', '1'];
const nanoBottom = ['13', '3.3V', 'AREF', 'A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', '5V', 'RESET.2', 'GND.2', 'VIN'];
function nanoLabel(name) {
  if (/^\d+$/.test(name)) return digitalLabel(Number(name));
  if (name.startsWith('GND')) return 'GND';
  if (name.startsWith('RESET')) return 'RESET';
  if (name === 'A6' || name === 'A7') return `${name} (solo analógica)`;
  if (name === 'A4') return 'A4 · SDA';
  if (name === 'A5') return 'A5 · SCL';
  if (name === 'VIN') return VIN_LABEL;
  return name === '3.3V' ? '3.3 V' : name === '5V' ? '5 V' : name;
}
function nanoKind(name) {
  if (name.startsWith('GND')) return 'gnd';
  if (['3.3V', '5V', 'VIN', 'AREF'].includes(name)) return 'power';
  if (name.startsWith('A') && name !== 'AREF') return 'analog';
  if (PWM.has(name)) return 'pwm';
  return /^\d+$/.test(name) ? 'digital' : 'signal';
}
const nanoPins = [];
nanoTop.forEach((name, i) => nanoPins.push({ name, label: nanoLabel(name), kind: nanoKind(name), x: (i - 7) * PITCH, y: 0, z: -3 * PITCH, insert: true }));
nanoBottom.forEach((name, i) => nanoPins.push({ name, label: nanoLabel(name), kind: nanoKind(name), x: (i - 7) * PITCH, y: 0, z: 3 * PITCH, insert: true }));

definePart({
  type: 'arduino-nano',
  name: 'Arduino Nano',
  help: 'Igual que el Uno: I²C en A4/A5, SPI en 10–13, interrupciones en 2 y 3. A6 y A7 solo leen voltajes (analogRead). ' +
    '<b>Alimentación</b>: USB (fusible de 500 mA y un diodo Schottky: el 5V queda en ~4.7 V) o 7–12 V en VIN (regulador LM1117); gana la más alta. ' +
    'El pin 3.3V sale del FT232RL y da hasta 50 mA. Sin cable USB no hay monitor serie.',
  category: 'placas',
  description: 'ATmega328P en formato pequeño para protoboard. Igual que el Uno, con A6 y A7 extra (solo analógicas).',
  kind: 'board',
  insertable: true,
  props: [USB_PROP],
  pins: nanoPins,
  buses: [['GND.1', 'GND.2'], ['RESET.1', 'RESET.2']],
  footprint: { w: NANO.W + 4, d: NANO.D, h: 8, x: -1 },
  mcu: {
    chip: 'atmega328p',
    fqbn: 'arduino:avr:nano',
    pins: AVR328_PINS,
    adc: { A0: 0, A1: 1, A2: 2, A3: 3, A4: 4, A5: 5, A6: 6, A7: 7 },
    power: { '5V': 5, '3.3V': 3.3, 'GND.1': 0, 'GND.2': 0 },
    /**
     * Nano 3.3 (esquemático oficial): USB → fusible MF-FSMF050 → Schottky SS1P3L (0.45 V máx. con 1 A) → 5V;
     * VIN → LM1117-5.0 → 5V (gana la fuente más alta); 3.3V del regulador interno del FT232RL. El regulador
     * casi no tiene cobre (θJA ≈ 125 °C/W, tabla del LM1117 con el pad mínimo). Consumo propio: ATmega328P
     * 15 mA (N. Gammon) + FT232RL 15 mA (hoja de datos) + LED PWR con 1 kΩ.
     */
    supply: {
      rails: {
        '5V': { pins: ['5V'], v: 5, cap: 7.7e-6 },
        '3.3V': { pins: ['3.3V'], v: 3.3, cap: 1e-6 },
      },
      mcu: { rail: '5V', load: 0.033, ...MCU_328 },
      usb: { rail: '5V', v: 5, fuse: 'MF-FSMF050', diode: { vf: 0.28, rs: 0.17 } },
      regulators: [
        { part: 'LM1117-5.0', from: { pin: 'VIN' }, to: '5V', rja: 125 },
        { part: 'FT232R-3V3OUT', from: { rail: '5V' }, to: '3.3V' },
      ],
      led: { rail: '5V', r: 1000 },
    },
    vin: 'VIN',
    aref: 'AREF',
    reset: 'RESET.1',
    builtinLed: '13',
    bus: { sda: 'A4', scl: 'A5', sck: '13', mosi: '11', miso: '12', ss: '10' },
  },
});

// Mega 2560 R3 -------------------------------------------------------------------------------------
export const MEGA = { W: 101.6, D: 53.34, PCB_Y: UNO.PCB_Y, PCB_T: UNO.PCB_T };
MEGA.PCB_TOP = MEGA.PCB_Y + MEGA.PCB_T;
MEGA.PIN_Y = MEGA.PCB_TOP + UNO.HEADER_H;
const megaX = (xm) => (xm - 2000) * MIL;
const megaZ = (ym) => -(ym - 1050) * MIL;
const MEGA_PWM = new Set([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 44, 45, 46]);
const MEGA_EXTRA = {
  0: 'RX0', 1: 'TX0', 2: 'INT4', 3: 'INT5', 14: 'TX3', 15: 'RX3', 16: 'TX2', 17: 'RX2', 18: 'TX1 · INT3', 19: 'RX1 · INT2',
  20: 'SDA · INT1', 21: 'SCL · INT0', 50: 'MISO', 51: 'MOSI', 52: 'SCK', 53: 'SS', 13: 'LED L',
};
const megaLabel = (n) => `D${n}${MEGA_PWM.has(n) ? ' ~PWM' : ''}${MEGA_EXTRA[n] ? ' · ' + MEGA_EXTRA[n] : ''}`;
const megaKind = (n) => (MEGA_PWM.has(n) ? 'pwm' : 'digital');
export const MEGA_AVR = {
  0: 'PE0', 1: 'PE1', 2: 'PE4', 3: 'PE5', 4: 'PG5', 5: 'PE3', 6: 'PH3', 7: 'PH4', 8: 'PH5', 9: 'PH6', 10: 'PB4', 11: 'PB5', 12: 'PB6',
  13: 'PB7', 14: 'PJ1', 15: 'PJ0', 16: 'PH1', 17: 'PH0', 18: 'PD3', 19: 'PD2', 20: 'PD1', 21: 'PD0', 22: 'PA0', 23: 'PA1', 24: 'PA2',
  25: 'PA3', 26: 'PA4', 27: 'PA5', 28: 'PA6', 29: 'PA7', 30: 'PC7', 31: 'PC6', 32: 'PC5', 33: 'PC4', 34: 'PC3', 35: 'PC2', 36: 'PC1',
  37: 'PC0', 38: 'PD7', 39: 'PG2', 40: 'PG1', 41: 'PG0', 42: 'PL7', 43: 'PL6', 44: 'PL5', 45: 'PL4', 46: 'PL3', 47: 'PL2', 48: 'PL1',
  49: 'PL0', 50: 'PB3', 51: 'PB2', 52: 'PB1', 53: 'PB0',
};
for (let a = 0; a < 16; a++) MEGA_AVR['A' + a] = (a < 8 ? 'PF' : 'PK') + (a % 8);

const megaPins = [];
{
  const add = (name, xm, ym, label, kind) => megaPins.push({ name, label, kind, x: megaX(xm), y: MEGA.PIN_Y, z: megaZ(ym) });
  add('SCL', 840, 2000, 'SCL (= D21)', 'signal');
  add('SDA', 940, 2000, 'SDA (= D20)', 'signal');
  add('AREF', 1040, 2000, 'AREF', 'power');
  add('GND.1', 1140, 2000, 'GND', 'gnd');
  for (let n = 13; n >= 8; n--) add(String(n), 1240 + (13 - n) * 100, 2000, megaLabel(n), megaKind(n));
  for (let n = 7; n >= 0; n--) add(String(n), 1900 + (7 - n) * 100, 2000, megaLabel(n), megaKind(n));
  for (let n = 14; n <= 21; n++) add(String(n), 2740 + (n - 14) * 100, 2000, megaLabel(n), megaKind(n));
  const bottom = [['IOREF', 1100, 'IOREF (5 V)', 'power'], ['RESET', 1200, 'RESET', 'signal'], ['3.3V', 1300, '3.3 V', 'power'],
    ['5V', 1400, '5 V', 'power'], ['GND.2', 1500, 'GND', 'gnd'], ['GND.3', 1600, 'GND', 'gnd'], ['VIN', 1700, VIN_LABEL, 'power']];
  for (const [name, xm, label, kind] of bottom) add(name, xm, 100, label, kind);
  for (let a = 0; a < 16; a++) add('A' + a, (a < 8 ? 1900 : 2000) + a * 100, 100, 'A' + a, 'analog');
  // Doble fila del extremo derecho: 5V arriba, 22–53 y GND abajo.
  add('5V.2', 3700, 2000, '5 V', 'power');
  add('5V.3', 3800, 2000, '5 V', 'power');
  for (let n = 22; n <= 53; n += 2) {
    const row = 1 + (n - 22) / 2;
    add(String(n), 3700, 2000 - row * 100, megaLabel(n), megaKind(n));
    add(String(n + 1), 3800, 2000 - row * 100, megaLabel(n + 1), megaKind(n + 1));
  }
  add('GND.4', 3700, 300, 'GND', 'gnd');
  add('GND.5', 3800, 300, 'GND', 'gnd');
}

definePart({
  type: 'arduino-mega',
  name: 'Arduino Mega 2560',
  help: 'PWM en 2–13 y 44–46. I²C: 20 (SDA) y 21 (SCL). SPI: 50 (MISO), 51 (MOSI), 52 (SCK), 53 (SS). Serie extra: Serial1 (18/19), Serial2 (16/17), Serial3 (14/15). Interrupciones: 2, 3, 18, 19, 20, 21. ' +
    '<b>Alimentación</b>: como el Uno (USB con fusible de 500 mA o 7–12 V en VIN; con más de 6.6 V en VIN deja de usar el USB), con regulador LD1117 ' +
    '(hasta 15 V en VIN y ~950 mA). A 16 MHz el ATmega2560 necesita 4.5 V. Sin cable USB no hay monitor serie.',
  category: 'placas',
  description: 'ATmega2560 a 16 MHz: 54 pines digitales (15 PWM), 16 entradas analógicas, 4 puertos serie y 8 KB de RAM.',
  kind: 'board',
  props: [USB_PROP],
  pins: megaPins,
  buses: [['20', 'SDA'], ['21', 'SCL'], ['GND.1', 'GND.2', 'GND.3', 'GND.4', 'GND.5'], ['5V', 'IOREF', '5V.2', '5V.3']],
  footprint: { w: MEGA.W + 8, d: MEGA.D, h: 14, x: -3 },
  mcu: {
    chip: 'atmega2560',
    fqbn: 'arduino:avr:mega',
    pins: MEGA_AVR,
    adc: Object.fromEntries(Array.from({ length: 16 }, (_, a) => ['A' + a, a])),
    power: { '5V': 5, IOREF: 5, '5V.2': 5, '5V.3': 5, '3.3V': 3.3, 'GND.1': 0, 'GND.2': 0, 'GND.3': 0, 'GND.4': 0, 'GND.5': 0 },
    // Consumo propio aproximado de un Mega con un sketch vacío: ~70 mA (reportes de usuarios). LED ON con 1 kΩ.
    supply: arduinoR3Supply({ ron: 0.048, reg: 'LD1117-5.0', load: 0.07, led: 1000, mcu: MCU_2560 }),
    vin: 'VIN',
    aref: 'AREF',
    reset: 'RESET',
    builtinLed: '13',
    bus: { sda: '20', scl: '21', sck: '52', mosi: '51', miso: '50', ss: '53' },
  },
});
